/**
 * Telling Brunel to do something, and asking Brunel something, are not the
 * same act.
 *
 * A command is planned: it becomes tasks, the owner approves them, a worker
 * runs them. A question is answered: nothing is dispatched and nothing waits
 * for approval. Sending a question to the planner produces the worst possible
 * reply - a plan of tasks whose only subject is the question itself - so the
 * rule lives here, in the protocol both the Overseer and Command read, rather
 * than in either of them.
 *
 * A question that asks for work in its own words ("can you fix the checkout?")
 * is still a command. #656: advice, feedback and context are answered - since
 * #644 a direct instruction runs without a second click, so reading them as an
 * order would start work the owner never asked for.
 *
 * THIS FILE IS THE OVERSEER'S COPY OF packages/shared/src/operations/questions.ts
 * and the two are compared byte for byte by a test. The Overseer compiles with
 * its own rootDir and takes no workspace dependency, so the rule is duplicated
 * rather than imported - and gated, because a rule that lives in two places and
 * is checked in neither is how the two sides end up disagreeing about what the
 * owner said.
 */

/** Recorded against a command that was answered rather than planned. */
export const QUESTION_NOT_A_COMMAND = 'question_not_a_command';

/** The words a question opens with when it is not punctuated. */
const OPENERS = [
  'who', 'what', 'whats', 'when', 'where', 'why', 'which', 'how', 'hows',
  'is', 'are', 'was', 'were', 'do', 'does', 'did', 'can', 'could', 'will',
  'would', 'should', 'am', 'any', 'anything', 'anyone', 'tell', 'show',
  'status', 'update',
];

/** Openers that ask for work in the shape of a question and ARE commands. */
const IMPERATIVE_AFTER_OPENER = [
  'make', 'build', 'fix', 'add', 'remove', 'delete', 'write', 'change', 'update',
  'create', 'run', 'deploy', 'start', 'stop', 'take', 'give', 'send', 'refresh',
  'merge', 'open', 'close', 'set',
];

/**
 * The rest of the verbs an order is given in. These are not counted loose in a
 * sentence the way the list above is - several of them are ordinary words in a
 * question ("do we have", "check the status") - so they only ever count at the
 * head of a clause, where a verb with no subject in front of it is an order.
 */
const IMPERATIVE_LEADING_A_CLAUSE = [
  'approve', 'cancel', 'check', 'clean', 'clear', 'continue', 'copy', 'do',
  'finish', 'install', 'kill', 'move', 'pause', 'pull', 'push', 'redo',
  'rename', 'rerun', 'restart', 'resume', 'retry', 'revert', 'try', 'unblock',
];

/** Words that come before the verb of an order without being it. */
const BEFORE_THE_VERB = [
  'also', 'and', 'can', 'could', 'finally', 'first', 'go', 'just', 'kindly',
  'next', 'now', 'please', 'so', 'then', 'we', 'will', 'would', 'you',
];

/** "do we", "do you" ask; "do it", "do that" tell. */
const ASKS_OF_SOMEONE = ['i', 'we', 'they', 'you'];

/** Said to Brunel rather than asked of it, and never a task. */
const SMALL_TALK = /^(hi|hey|hello|yo|good (morning|afternoon|evening)|morning|evening|thanks|thank you|cheers)\b/;

/** Longer than this and it is not a greeting, it is a greeting with an order after it. */
const SMALL_TALK_MAX = 24;

function words(value: string): string[] {
  return value.toLowerCase().replace(/[^a-z0-9\s]/g, ' ').split(/\s+/).filter((w) => w !== '');
}

/**
 * The command cut where one instruction ends and the next begins: at any
 * punctuation, and at the words people join two orders with. "what failed and
 * go fix it and then run it again" is three of them, and only the first asks
 * anything.
 */
function clauses(value: string): string[][] {
  return value
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9\s]/g, '|')
    .split(/\|+|\band then\b|\bafter that\b|\band\b|\bthen\b/)
    .map((part) => part.split(/\s+/).filter((w) => w !== ''))
    .filter((part) => part.length > 0);
}

/**
 * Does this clause give an order? It does when, once the words that come before
 * a verb are out of the way ("go", "then", "can you"), what is left starts with
 * one - "fix it", "go fix it", "then run it again". A clause that starts with
 * anything else is not an order, whatever verbs appear later inside it.
 */
function isOrder(clause: string[]): boolean {
  let i = 0;
  while (i < clause.length && BEFORE_THE_VERB.includes(clause[i]!)) i += 1;
  const verb = clause[i];
  if (verb === undefined) return false;
  if (!IMPERATIVE_AFTER_OPENER.includes(verb) && !IMPERATIVE_LEADING_A_CLAUSE.includes(verb)) return false;
  if (verb === 'do' && ASKS_OF_SOMEONE.includes(clause[i + 1] ?? '')) return false;
  return true;
}

/**
 * Does this command ask rather than tell?
 *
 * Asking looks like: it ends in a question mark, or it opens with one of the
 * question words and does not go on to ask for work. "can you fix the
 * checkout?" opens like a question and is an order; "what is worker 2 doing?"
 * is not. A command naming a worker to act - "3 stop", "1 take the fix" - is
 * never a question, whatever punctuation it carries. A short greeting is not a
 * task either: a plan of tasks is a worse answer to "hello" than a word is,
 * while "thanks for the plan, now deploy it" is an order with manners on it.
 *
 * A message can do both. "what failed and go fix it and then run it again"
 * opens with a question word and asks about failure, but it goes on to give two
 * orders, and the owner who typed it meant them. Once any clause of it is an
 * order the whole message is told, not asked: the answer path has nothing to
 * dispatch, so reading that as a question does nothing at all.
 */
function legacyAsks(command: string): boolean {
  const trimmed = command.trim();
  if (trimmed === '') return false;

  const parts = words(trimmed);
  if (parts.length === 0) return false;

  // "3 stop", "2 refresh" - an order addressed to a numbered worker.
  if (/^\d+\s/.test(trimmed)) return false;

  if (trimmed.length <= SMALL_TALK_MAX && SMALL_TALK.test(trimmed.toLowerCase())) return true;

  const opensAsQuestion = OPENERS.includes(parts[0]!);
  const endsAsQuestion = trimmed.endsWith('?');
  if (!opensAsQuestion && !endsAsQuestion) return false;

  // "...and go fix it and then run it again" - an order stands on its own
  // however the message opened, and is not undone by the question in front of it.
  if (clauses(trimmed).some(isOrder)) return false;

  // "can you fix the checkout" / "could you deploy this?" - the question mark is
  // manners; the verb is an order.
  if (parts.some((word) => IMPERATIVE_AFTER_OPENER.includes(word))) {
    // ...unless it is asking ABOUT that work rather than for it.
    const asksAbout = /\b(what|who|when|where|why|which|how|is|are|did|does|status)\b/.test(parts.join(' '))
      && /\b(doing|done|running|finished|failed|waiting|idle|left|going)\b/.test(parts.join(' '));
    if (!asksAbout) return false;
  }

  return true;
}

/**
 * #644: what the owner is doing with this message. The owner corrected Brunel
 * after live use: Main Command is a conversation, and an explicit instruction
 * to DO something is itself the authority for that exact work.
 *
 * - conversation: questions, status, small talk - answered, never planned.
 * - correction: context about work already under way ("you ran it on Windows,
 *   not the Mac") - answered, and applied to that chain, never a new plan.
 * - direct_action: "close Worker 4", "start Claude on the MacBook" - done
 *   without asking the owner to approve a plan for what they just ordered.
 * - deliberate_plan: "make me a plan", "options before changing anything" -
 *   proposed and left for the owner to decide.
 *
 * #656: a plan only when the owner asks for one, and work only when the owner
 * orders it. Advice ("what should we do?"), feedback ("I don't like what
 * happened") and context ("the build is broken") are conversation; a clause
 * that gives an order, or asks for one ("I want you to close Worker 4"), is
 * still direct_action.
 *
 * Mirrors brunel_private.command_intent; both are tested against one fixture.
 */
export type CommandIntent = 'conversation' | 'correction' | 'direct_action' | 'deliberate_plan';

/** A whole-message read request. Listing roadmap rows never creates Worker work. */
export function roadmapReadQuestion(command: string): 'current' | 'all' | null {
  const n = command.toLowerCase().replace(/['’]/g, '').replace(/[^a-z0-9 ]/g, ' ').replace(/ +/g, ' ').trim()
    .replace(/^(?:hey |hi |ok |okay )?(?:brunel |overseer )?(?:please )?/, '');
  if (n.length === 0 || n.length > 180) return null;
  if (/^(?:show|list|display)(?: me)? (?:the |our )?(?:current |active )?(?:jobs|items|work|tasks) (?:on|in) (?:the |our )?roadmap$/.test(n)
    || /^(?:show|list|display)(?: me)? (?:the |our )?(?:current |active )?roadmap (?:jobs|items|work|tasks)$/.test(n)
    || /^(?:what|which) (?:jobs|items|work|tasks) (?:are )?(?:currently |now )?(?:on|in) (?:the |our )?roadmap$/.test(n)
    || /^(?:what is|whats) (?:on )?(?:the |our )?(?:current |active )?roadmap$/.test(n)) return 'current';
  if (/^(?:show|list|display)(?: me)? (?:the |our )?(?:whole |entire |full )?roadmap$/.test(n)) return 'all';
  return null;
}

const DELIBERATE = [
  /\b(make|give|draft|write|prepare|propose|suggest|outline|sketch) (me |us )?(a |an |the |some )?([a-z-]+ )?(plan|options|alternatives|proposal|approach)\b/,
  /^(please |lets |let us |can you |could you |would you )?plan\b/,
  /\b(before (changing|doing|touching|you change|you do|you start)|and wait|(do not|dont) (do|change|execute|start|run|touch) (anything|it|yet|this)|without (changing|executing|doing) anything)\b/,
];

/** "I want you to close Worker 4" is an order in the shape of a wish. */
const WANTS_IT_DONE = /\b(?:(?:i|we) (?:want|need|would like)|id like) (?:you|brunel|worker [0-9]+|the overseer) to ([a-z]+)/;

/** ...unless what is wanted is to be told or understood: "I want you to know it failed". */
const WANTED_NOT_WORK = [
  'answer', 'be', 'consider', 'explain', 'feel', 'know', 'note', 'realise', 'realize',
  'remember', 'say', 'see', 'show', 'tell', 'think', 'understand',
];

/**
 * #656: feedback and context open with what they are about - "I don't like what happened",
 * "the build is broken", "Worker 4 is stuck". An order opens with its verb, including verbs
 * no list here names ("bring", "hire", "implement"), so only this shape is read as said.
 */
const STATEMENT = /^(i|im|ive|id|ill|we|were|weve|wed|you|youre|youve|it|its|that|thats|this|these|those|there|theres|here|heres|the|my|our|your|his|her|their|he|she|they|theyre|everything|nothing|something|someone|nobody|everyone|ok|okay|hmm|well|honestly|sorry|great|nice|cool|good|fine|sure|yes|yeah|yep|worker [0-9]+ (is|was|has|had|looks|seems|keeps|isnt|wasnt|hasnt|didnt|did))\b/;

const CORRECTION = [
  /^(and |but )?(no|nope|wrong|not that|i meant|i said|i asked|that was not|that wasnt|that is not|thats not|it was not|it wasnt|that was the wrong)\b/,
  /\b(you|it|brunel) (started|ran|used|picked|chose|sent|opened|launched|assigned|put|gave)\b.*\b(not|instead|wrong)\b/,
  /^(and |but )?(that|it|this) (was|is) [a-z0-9]+( [a-z0-9]+)? not [a-z0-9]+$/,
];

/**
 * "can you check Worker 4 and tell me what it is doing?" asks to be told. Checking in order
 * to report back is not work to dispatch; "check Worker 4 and close it" still is.
 */
const REPORT_BACK = /\b(tell me|let me know|show me)\b/;

/**
 * "which workers can run a claude task right now?" asks about work, it does not order it: a
 * question word, a question mark, and no clause that starts with an order.
 */
const WH_OPENER = /^(who|what|whats|which|when|where|why|how|hows)\b/;

/** "can you already accept tasks and send tasks out?" asks what Brunel can do. */
const ASKS_CAPABILITY = /^(can|could|will|do|does|are|is) (you|brunel|it|we|they|the overseer|overseer) (already|yet|still|currently|now|actually|even|really|able to)\b/;

function normalized(value: string): string {
  return value.toLowerCase().replace(/['’]/g, '').replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * The owner saying in so many words that this is talk, not work: "this is a discussion, not a
 * build order". At 04:03 that message still became three Worker tasks, because its own
 * evidence list "(run, screen or message)" carries a clause that starts with "run".
 */
const DISCUSSION = /\b(?:not an? (?:build |work )?(?:order|task|request)|(?:this|it) is (?:just |only )?a discussion|discussion (?:only|not)|just (?:a )?discussion|just discussing|(?:do not|dont) build (?:anything|it|this|yet))\b/;

/**
 * Work asked for in the shape of a question: "is it possible to add a filter?", "why don't you
 * fix the checkout?". The question mark is manners; the verb is an order.
 */
const WORK_ASKED = /\b(?:(?:is|was) it possible|(?:would|will|could) it be possible|any chance (?:you|we|brunel) (?:could|can)|why (?:dont|not|cant|wont) (?:you|we|brunel)|how about (?:you|we)|is there (?:a|any) way(?: (?:you|we|brunel) (?:could|can))?)(?: (?:please|just|also|now|then))*(?: to)? (?:make|build|fix|add|remove|delete|write|change|update|create|run|deploy|start|stop|take|give|send|refresh|merge|open|close|set|approve|cancel|check|clean|clear|continue|copy|finish|install|kill|move|pause|pull|push|redo|rename|rerun|restart|resume|retry|revert|try|unblock|implement|improve|limit|hide|show|sort)\b/;

/**
 * Asking the roles for their views - "reply with your top 3 improvements", "give your next top 3
 * each", "share your thoughts" - is a discussion the roles answer, not work to dispatch. Read on
 * the lowercased text with its punctuation, so the clauses around it still split.
 */
const VIEWS_ASKED = /\b(?:give|share|reply with|send|tell (?:me|us)|list|offer|post|what are|whats)(?: me| us)? (?:your|their|each of your)(?: [a-z0-9]+){0,4} (?:ideas?|thoughts?|opinions?|views?|takes?|suggestions?|recommendations?|improvements?|perspectives?|feedback|advice|input|top [0-9]+|priorities)\b/g;

/** Views asked for, and no other clause that orders work ("...and then build them" still does). */
function asksForViews(command: string): boolean {
  const said = command.toLowerCase().replace(/['’]/g, '');
  if (said.match(VIEWS_ASKED) === null) return false;
  return !clauses(said.replace(VIEWS_ASKED, 'views')).some(isOrder);
}

/** "plan the migration first" is a short phrase, not "plan" and any later "first". */
const PLAN_FIRST = /\bplan( [a-z0-9]+){0,5} first\b/;

/** A file name or path ("plan.ts", "lib/plan") is not the word "plan". */
function prose(value: string): string {
  return normalized(value.toLowerCase().replace(/['’]/g, '').replace(/[a-z0-9_-]*[./\\][a-z0-9_./\\-]*[a-z0-9]/g, ' '));
}

export function commandIntent(command: string): CommandIntent {
  const trimmed = command.trim();
  if (trimmed === '' || /^\d+\s/.test(trimmed)) return 'direct_action';
  if (roadmapReadQuestion(trimmed) !== null) return 'conversation';
  if (trimmed.length <= SMALL_TALK_MAX && SMALL_TALK.test(trimmed.toLowerCase())) return 'conversation';
  const n = normalized(trimmed);
  if (DELIBERATE.some((pattern) => pattern.test(n)) || PLAN_FIRST.test(prose(trimmed))) return 'deliberate_plan';
  if (CORRECTION.some((pattern) => pattern.test(n))) return 'correction';
  if (DISCUSSION.test(n)) return 'conversation';
  if (WORK_ASKED.test(n)) return 'direct_action';
  if (asksForViews(trimmed)) return 'conversation';
  // A request for the team to report its status is a conversation, not Worker work.
  // Keep this whole-message only: "team report in, then deploy" still orders work.
  if (/^team report in(?: please)?$/.test(n)) return 'conversation';
  if (/^(explain|describe|summarize|summarise)\b/.test(n) && !clauses(trimmed).some(isOrder)) return 'conversation';
  if ((trimmed.endsWith('?') || /^(can|could|will|do|does|are|is) /.test(n)) && ASKS_CAPABILITY.test(n)) return 'conversation';
  if (trimmed.endsWith('?') && WH_OPENER.test(n) && !clauses(trimmed).some(isOrder)) return 'conversation';
  if (REPORT_BACK.test(n) && legacyAsks(trimmed.replace(/\bcheck\b/gi, 'look at'))) return 'conversation';
  if (legacyAsks(trimmed)) return 'conversation';
  if (clauses(trimmed).some(isOrder)) return 'direct_action';
  // "is there anything to fix?" asks; only a clause that orders the fixing would tell.
  if (trimmed.endsWith('?')) return 'conversation';
  const wanted = WANTS_IT_DONE.exec(n)?.[1];
  if (wanted !== undefined) return WANTED_NOT_WORK.includes(wanted) ? 'conversation' : 'direct_action';
  // #656: saying something is not ordering it.
  return STATEMENT.test(n) ? 'conversation' : 'direct_action';
}

/**
 * Does this command ask rather than tell? Conversation and corrections are
 * answered; direct actions and deliberate plans are planned.
 */
export function isQuestion(command: string): boolean {
  const intent = commandIntent(command);
  return intent === 'conversation' || intent === 'correction';
}
